import { Paginated, Query } from "@feathersjs/feathers/lib";
import { addWeeks, startOfMonth, endOfMonth, startOfDay } from "date-fns";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { getRoutePath, Page, RoomView } from "src/routes";
import { RootStore, ItemStore } from ".";
import { Question } from "../models";
import { DataType, IQuestion, QuestionPosition } from "../types";
import { DataContainer } from "./dataContainer";


export class QuestionStore extends ItemStore<Question, IQuestion, QuestionStore.ContainerType> {
  dashboardFrom:  boolean = true;
  positionCounts: Partial<Record<QuestionPosition, number>> = {};
  filterProps:    (keyof Question | string)[] = [
    "from", "to", "position", "answered", "critical", "tags", "deliverables"
  ];
  defaultFilter: {[prop: string]: any[]} = {
    position: [{ key: 'board'}]
  };
  protected readonly DashboardContainers: QuestionStore.ContainerType[] = [
    QuestionStore.ContainerType.FromCrit,
    QuestionStore.ContainerType.ToCrit,
    QuestionStore.ContainerType.FromPrio,
    QuestionStore.ContainerType.ToPrio,
  ];

  constructor(readonly rootStore: RootStore) {
    super(rootStore, Question.create, DataType.Question, [DataType.Deliverable, DataType.Question]);

    makeObservable(this, {
      dashboardFrom:             observable,
      positionCounts:            observable,
      getDashboardFilterFrom:    computed,
      toggleDashboardFilterFrom: action,
    });
  }

  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/
  init() {
    super.init();

    // Initial Question Count: 
    if(this.rootStore.authStore.getCurRoomID) this.getPositionCounts();

    // Reactions: 
    reaction(() => this.dashboardFrom, () => {
      if(this.rootStore.spaceStore.onDashboard) this.loadDashboard();
    }, {name: "question_dashboardFrom_loadDashboard"});

    reaction(() => this.rootStore.authStore.getCurRoomID, () => {
      if(!this.rootStore.authStore.getCurRoomID) this.positionCounts = {};
      else this.getPositionCounts();
    })

    // Register event callbacks
    this.eventCallBacks.before.patched.push(this.rootStore.meetingStore.countAffectedItems);
    this.eventCallBacks.after.patched.push(this.getPositionCounts);
    this.eventCallBacks.after.updated.push(this.getPositionCounts);
    this.eventCallBacks.after.all.push(this.roomFilter);
    this.eventCallBacks.after.created.push((data: IQuestion) => runInAction(() => this.positionCounts[data.position]++));
    this.eventCallBacks.after.removed.push((data: IQuestion) => runInAction(() => this.positionCounts[data.position]--));
  }

  loadRoom = () => {
    super.loadRoom();
    this.storage.board.load();
  }

  loadDashboard = () => {
    super.loadDashboard();
    this.query = {
      "$or": [
        {"question.from": {$in: this.rootStore.authStore.getCurUser.getUserGroups}},
        {"question.to":   {$in: this.rootStore.authStore.getCurUser.getUserGroups}},
      ],
      date: {
        $gte: addWeeks(startOfMonth(this.rootStore.spaceStore.curDate), -2).toISOString(),
        $lte: addWeeks(endOfMonth(this.rootStore.spaceStore.curDate),    2).toISOString(),
      },
    };
    this.storage.fromCrit.load();
    this.storage.fromPrio.load();
    this.storage.toCrit.load();
    this.storage.toPrio.load();
    this.storage.to.load();
    this.storage.from.load();
    this.storage.board.clear();
  }
  clearDashboard = () => {
    this.storage.fromCrit.clear();
    this.storage.fromPrio.clear();
    this.storage.toCrit.clear();
    this.storage.toPrio.clear();
    this.storage.to.clear();
    this.storage.from.clear();
  }

  setActive(q?: IQuestion): void {
    if(this.rootStore.meetingStore.getActiveMeeting && q) {
      const newQ = this.create(q);
      if(newQ.isRelevantForMeeting) newQ.set("processed", true, !newQ.isNew);
      this.activeStack.push(newQ);
    } else {
      if(q) this.activeStack.push(this.create(q));
      else this.activeStack.pop();
    }
  }

  /*******************************************************************************************************************************************
   * Database Access 
   ********************************************************************************************************************************************/
  protected setupStorage() {
    super.setupStorage();
    
    // Hold next 5 user Questions in the future
    for(const t of this.DashboardContainers) {
      const query = this.getQueries[t];
      this.storage[t] = 
        new DataContainer<IQuestion, Question>(
            query,
            this.service,
            { // Config
              paginate:    true,
              pageSize:    query?.$limit ?? 10,
              limited:     false,
              eventFilter: this.getEventFilter[t],
              conversion:  Question.create,
              name: this.data_type + "." + t,
            }
        );
      this.storage[t].addEventCallbacks(this.eventCallBacks);
      reaction(() => this.rootStore.authStore.getCurUserID, () => this.storage[t].setQuery(this.getQueries[t]));
    }
      
    // Get Board questions
    let t = QuestionStore.ContainerType.Board;
    this.storage[t] = 
      new DataContainer<IQuestion, Question>(
          this.getQueries[t], 
          this.service,
          { // Config
            paginate:    false,
            limited:     false,
            eventFilter: this.getEventFilter[t],
            conversion:  Question.create,
            name: this.data_type + "." + t,
          }
      );
    
    this.storage[t].addEventCallbacks(this.eventCallBacks);
    reaction(() => this.rootStore.authStore.getCurRoomID, () => {
      this.storage[t].setQuery(this.getQueries[t])
    });
    
    // Get TO questions
    t = QuestionStore.ContainerType.To;
    this.storage[t] = 
      new DataContainer<IQuestion, Question>(
          this.getQueries[t], 
          this.service,
          { // Config
            paginate:    false,
            limited:     false,
            eventFilter: this.getEventFilter[t],
            conversion:  Question.create,
            name: this.data_type + "." + t,
          }
      );
    this.storage[t].addEventCallbacks(this.eventCallBacks);
    reaction(() => this.rootStore.authStore.getCurRoomID, () => this.storage[t].setQuery(this.getQueries[t]));

    // Get FROM questions
    t = QuestionStore.ContainerType.From;
    this.storage[t] = 
      new DataContainer<IQuestion, Question>(
          this.getQueries[t], 
          this.service,
          { // Config
            paginate:    false,
            limited:     false,
            eventFilter: this.getEventFilter[t],
            conversion:  Question.create,
            name: this.data_type + "." + t,
          }
      );
    this.storage[t].addEventCallbacks(this.eventCallBacks);
    reaction(() => this.rootStore.authStore.getCurRoomID, () => this.storage[t].setQuery(this.getQueries[t]));
  }

  protected getPositionCounts = async () => {
    for(const p of Object.values(QuestionPosition)) {
      const count: Paginated<IQuestion> = (await this.service.find({query: {
        $limit:   0,
        position: p,
        room_id:  this.rootStore.authStore.getCurRoomID
      }})) as unknown as Paginated<IQuestion>;
      runInAction(() => this.positionCounts[p] = count.total);
    }
  }
  /*******************************************************************************************************************************************
   * Getter 
   ********************************************************************************************************************************************/
  get getDashboardFilterFrom(): boolean {
    return this.dashboardFrom;
  }

  get getQueries(): {[name: string]: Query} {
    const prioQ = {
      "question.critical": false,
      "question.position": {$in: [QuestionPosition.Before, QuestionPosition.Board]},
      "question.answered": null as any,
      date:    {$lte: startOfDay(new Date()).toISOString()},
      $sort:   {"date": 1},
      $limit:  this.overviewLimit,
    };
    const critQ = {
      "question.critical": true,
      "question.position": {$in: [QuestionPosition.Before, QuestionPosition.Board]},
      "question.answered": null as any,
      $sort:   {"date": 1},
      $limit:  this.overviewLimit,
    };

    return {
      [QuestionStore.ContainerType.FromCrit]: {
        ...critQ,
        "question.from": {$in: this.rootStore.authStore.getCurUser?.getUserGroups || []},
      },
      [QuestionStore.ContainerType.ToCrit]:   {
        ...critQ,
        "question.to": {$in: this.rootStore.authStore.getCurUser?.getUserGroups || []},
      },
      [QuestionStore.ContainerType.FromPrio]: {
        ...prioQ,
        "question.from": {$in: this.rootStore.authStore.getCurUser?.getUserGroups || []},
      },
      [QuestionStore.ContainerType.ToPrio]:   {
        ...prioQ,
        "question.to": {$in: this.rootStore.authStore.getCurUser?.getUserGroups || []},
      },
      [QuestionStore.ContainerType.Board]: {
        "question.position": QuestionPosition.Board,
        "question.to":       {$ne: null},
        "question.from":     {$ne: null},
        "question.room_id":  this.rootStore.authStore.getCurRoomID,
      },
      [QuestionStore.ContainerType.To]: {
        "question.critical": false,
        "question.position": {$in: [QuestionPosition.Before, QuestionPosition.Board] },
        "question.to":    {$in: this.rootStore.authStore.getCurUser?.getUserGroups || []},
        "question.date":  {$gte: new Date().toISOString() },
      },
      [QuestionStore.ContainerType.From]: {
        "question.critical": false,
        "question.position": {$in: [QuestionPosition.Before, QuestionPosition.Board]},
        "question.from":     {$in: this.rootStore.authStore.getCurUser?.getUserGroups || []},
        "question.date":     {$gte: new Date().toISOString() },
      }
    }
  }

  get getEventFilter(): {[name: string]: (q: IQuestion) => boolean} { // TODO check if this also needs to be updated in the store when the ID changes
    return {
      [QuestionStore.ContainerType.FromCrit]: (q: IQuestion) => {
        const _q = Question.create(q);
        return !_q.archived && _q.isCritical && this.rootStore.authStore.getCurUser.getUserGroups.includes(Number(_q.from))
      },
      [QuestionStore.ContainerType.ToCrit]:   (q: IQuestion) => {
        const _q = Question.create(q);
        return !_q.archived && _q.isCritical  && this.rootStore.authStore.getCurUser.getUserGroups.includes(Number(_q.to))
      },
      [QuestionStore.ContainerType.FromPrio]: (q: IQuestion) => {
        const _q = Question.create(q);
        return !q.archived && _q.isPrio && this.rootStore.authStore.getCurUser.getUserGroups.includes(Number(q.from))
      },
      [QuestionStore.ContainerType.ToPrio]:   (q: IQuestion) => {
        const _q = Question.create(q);
        return !q.archived && _q.isPrio && this.rootStore.authStore.getCurUser.getUserGroups.includes(Number(q.to))
      },
      [QuestionStore.ContainerType.Board]:    (q: IQuestion) => !q.archived && q.from && q.to && q.position === QuestionPosition.Board,
    }
  }

  /*******************************************************************************************************************************************
   * Setter 
   ********************************************************************************************************************************************/

  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/

  toggleDashboardFilterFrom = () => {
    this.dashboardFrom = !this.dashboardFrom;
  }

  /**
   * Opens the question with @ID by routing to the corresponding room and setting the question as active question.
   * @param id 
   */
  openQuestionInRoom = async(id: number) => {
    if(this.container[id]) {
      this.rootStore.routingStore.push(getRoutePath(Page.Room, {id: this.container[id].room_id, view: RoomView.Question})),
      this.setActive(this.container[id]);
    } else {
      const q = Question.create(await this.service.get(id));
      this.rootStore.routingStore.push(getRoutePath(Page.Room, {id: q.room_id, view: RoomView.Question})),
      this.setActive(q);
    }
  }

  roomFilter = (data: IQuestion) => { 
    if(!this.rootStore.authStore.getCurRoomID) return;

    if(Number(data.room_id) !== Number(this.rootStore.authStore.getCurRoomID)) {
      runInAction(() => {
        delete this.container[data.id];
        this.container = Object.assign({}, this.container);
      })
    }
  }
}

export namespace QuestionStore {
  export enum ContainerType {
    FromPrio = "fromPrio",
    FromCrit = "fromCrit",
    ToPrio   = "toPrio",
    ToCrit   = "toCrit",
    Board    = "board",
    To  = "to",
    From  = "from",
  }
}
